import { useEffect, useRef } from "react";

/**
 * A layer that the phone's Back gesture closes, rather than leaving the page under it.
 *
 * On the phone the app is a WebView and Back is `goBack()` on its history, so a sheet that lives
 * only in React state is invisible to it: Back skips past the thing on screen and leaves the tab
 * the reader was in. The fix is to put the layer *in* that history — one entry, pushed as it
 * opens — so Back has something of its own to pop.
 *
 * Closing any other way (the ✕, Escape, the backdrop) takes the entry off again, so a sheet
 * opened and shut a dozen times leaves no trail to walk back through.
 *
 * Layers nest the way they are stacked: the full-screen picture pushes its entry on top of the
 * pager's, so the first Back closes the picture and the second closes the pager.
 */
/**
 * Entries this module is taking off the stack itself. A layer shut by its own ✕ pops the entry
 * it pushed, and that pop must not be read by the layer underneath as the reader pressing Back —
 * which would close that one too, and the whole stack with it.
 */
let unwinding = 0;

/**
 * The layers on screen, innermost last. A popstate reaches every listener, so without this the
 * one Back meant for the picture on top would close the sheet underneath it as well — the whole
 * stack unwinding on a single press.
 */
const stack: object[] = [];

export function useBackLayer(onBack: () => void) {
  const back = useRef(onBack);
  back.current = onBack;

  useEffect(() => {
    // Marked, so a popstate from somewhere else is not mistaken for this layer's own.
    const mark = `layer-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const me = {};
    let mine = true;
    let open = false;

    /*
     * The entry goes on a microtask later than the effect that asks for it.
     *
     * A layer that is mounted and immediately torn down and mounted again — which is what React
     * does to every effect in development, and what any remount does — otherwise pushed an
     * entry, went back for it on the way out, and pushed a second one while that first pop was
     * still in flight. The pop then arrived at a layer that had never asked for it, and since it
     * was the only one on the stack, it closed itself on the spot: the full-size picture opened
     * and vanished in the same breath. Deferred, the discarded mount never pushes at all, so
     * there is no stray pop to arrive — and the one that survives pushes exactly once.
     */
    queueMicrotask(() => {
      if (!mine) return;
      // Nothing is open, so nothing can be waiting for a pop of its own: a count left over from
      // a layer whose unwind never arrived would otherwise swallow this one's first Back.
      if (stack.length === 0) unwinding = 0;
      stack.push(me);
      window.history.pushState({ mark }, "");
      open = true;
    });

    const onPop = () => {
      if (unwinding > 0) {
        unwinding--;
        return;
      }
      // Only the layer on top answers a Back; the ones beneath keep their entries.
      if (stack[stack.length - 1] !== me) return;
      stack.pop();
      // Back took the entry with it, so the cleanup below has nothing to go back for.
      open = false;
      back.current();
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      const i = stack.indexOf(me);
      if (i >= 0) stack.splice(i, 1);
      // Gone before its entry was ever pushed: the microtask above sees this and pushes nothing,
      // so there is nothing here to take off either.
      const pushed = open;
      mine = false;
      // Shut by a control rather than by Back: the entry is still on the stack, so take it off.
      if (pushed && (window.history.state as { mark?: string } | null)?.mark === mark) {
        unwinding++;
        window.history.back();
      }
    };
  }, []);
}
